import React, { useState } from 'react'
import axios from 'axios'
import { DateRangePicker } from 'rsuite'
import { CSVLink } from 'react-csv'

const DocketReport = () => {
  const [dateRange, setDateRange] = useState(null)
  const [error, setError] = useState('')
  const [loadingDate, setLoadingDate] = useState(false)
  const [reportRows, setReportRows] = useState([])

  const formatDisplayDate = (date) => {
    if (!date) return ''
    const d = new Date(date)
    if (Number.isNaN(d.getTime())) return ''

    let month = '' + (d.getMonth() + 1)
    let day = '' + d.getDate()
    const year = d.getFullYear()

    if (month.length < 2) month = '0' + month
    if (day.length < 2) day = '0' + day

    return [day, month, year].join('/')
  }

  const formatFileDate = (date) => {
    if (!date) return ''
    const d = new Date(date)
    if (Number.isNaN(d.getTime())) return ''

    let month = '' + (d.getMonth() + 1)
    let day = '' + d.getDate()
    const year = d.getFullYear()

    if (month.length < 2) month = '0' + month
    if (day.length < 2) day = '0' + day

    return [year, month, day].join('-')
  }

  const formatDateRange = (range) => {
    if (!range || !Array.isArray(range) || range.length !== 2) return ''
    return `${formatFileDate(range[0])} to ${formatFileDate(range[1])}`
  }

  const parseDateOnly = (value) => {
    if (!value) return null

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return new Date(value.getFullYear(), value.getMonth(), value.getDate())
    }

    if (typeof value === 'string') {
      const trimmed = value.trim()

      // Common backend format from docket_date input controls
      const isoDateOnly = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (isoDateOnly) {
        const year = Number(isoDateOnly[1])
        const month = Number(isoDateOnly[2]) - 1
        const day = Number(isoDateOnly[3])
        return new Date(year, month, day)
      }

      // ISO datetime, keep only date portion
      const isoDateTime = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})T/)
      if (isoDateTime) {
        const year = Number(isoDateTime[1])
        const month = Number(isoDateTime[2]) - 1
        const day = Number(isoDateTime[3])
        return new Date(year, month, day)
      }

      // Friendly format sometimes seen in transformed payloads
      const slashDate = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
      if (slashDate) {
        const day = Number(slashDate[1])
        const month = Number(slashDate[2]) - 1
        const year = Number(slashDate[3])
        return new Date(year, month, day)
      }

      const dashDate = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/)
      if (dashDate) {
        const day = Number(dashDate[1])
        const month = Number(dashDate[2]) - 1
        const year = Number(dashDate[3])
        return new Date(year, month, day)
      }
    }

    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return null
    return new Date(d.getFullYear(), d.getMonth(), d.getDate())
  }

  const toDateOnlyTime = (value) => {
    const d = parseDateOnly(value)
    return d ? d.getTime() : null
  }

  const buildReportRows = (orders, range) => {
    if (!Array.isArray(orders)) return []

    const rows = []
    const seenRows = new Set()
    const hasRange = Array.isArray(range) && range.length === 2
    const rangeStart = hasRange ? toDateOnlyTime(range[0]) : null
    const rangeEnd = hasRange ? toDateOnlyTime(range[1]) : null

    orders.forEach((order) => {
      const orderList = Array.isArray(order?.order_list) ? order.order_list : []
      const orderNumber = order?.order_number ?? order?.id ?? ''
      const orderDate = order?.created_at ? formatDisplayDate(order.created_at) : ''
      const orderType = order?.ordertype?.name ?? ''
      const customerAccountName =
        order?.customer?.account_contact_name ?? order?.customer?.account_name ?? ''
      const customerTerm = order?.customer?.customer_terms?.name ?? ''

      const docketGroups = orderList.length > 0 ? orderList : [order]

      docketGroups.forEach((group) => {
        const dockets = Array.isArray(group?.docket_numbers)
          ? group.docket_numbers
          : Array.isArray(order?.docket_numbers)
          ? order.docket_numbers
          : []

        dockets.forEach((docket) => {
          const docketNumber = docket?.docket_number ?? docket?.docketNumber ?? ''
          const docketDateRaw = docket?.docket_date ?? docket?.docketDate ?? docket?.date ?? null
          const docketDateTime = toDateOnlyTime(docketDateRaw)

          if (!String(docketNumber).trim()) {
            return
          }

          if (!docketDateRaw || !String(docketDateRaw).trim()) {
            return
          }

          if (
            hasRange &&
            (docketDateTime === null || docketDateTime < rangeStart || docketDateTime > rangeEnd)
          ) {
            return
          }

          const rowKey = [docketNumber, docketDateRaw ?? '', docket?.weight ?? ''].join('|')
          if (seenRows.has(rowKey)) {
            return
          }
          seenRows.add(rowKey)

          rows.push({
            order_number: orderNumber,
            order_date: orderDate,
            customer_account_name: customerAccountName,
            customer_term: customerTerm,
            order_type: orderType,
            docket_number: docketNumber,
            docket_date: docketDateRaw ? formatDisplayDate(docketDateRaw) : '',
            location: docket?.skulocation?.name ?? docket?.sku_location?.name ?? '',
            weight: docket?.weight ?? '',
            tons_ordered: order?.tons_ordered ?? '',
            tons_actual: order?.tons_actual ?? '',
            file: docket?.imageURL ?? '',
            file_name: docket?.imageName ?? docketNumber,
            order_sort_date: order?.created_at ? new Date(order.created_at).getTime() : 0,
            docket_sort_date: docketDateTime ?? 0,
          })
        })
      })
    })

    return rows.sort(
      (a, b) =>
        a.docket_sort_date - b.docket_sort_date ||
        String(a.docket_number).localeCompare(String(b.docket_number)),
    )
  }

  const collectOrders = (source) => {
    const found = []
    const seen = new Set()

    const visit = (node) => {
      if (!node) return

      if (Array.isArray(node)) {
        node.forEach((item) => visit(item))
        return
      }

      if (typeof node !== 'object') return

      const hasDockets =
        Array.isArray(node?.docket_numbers) ||
        (Array.isArray(node?.order_list) &&
          node.order_list.some((group) => Array.isArray(group?.docket_numbers)))

      if (hasDockets) {
        const key = String(node.id ?? `${node.order_number ?? ''}-${node.created_at ?? ''}`)
        if (!seen.has(key)) {
          seen.add(key)
          found.push(node)
        }
        // Stop descending — child nodes (e.g. order_list groups) would be
        // re-collected as fake orders with no order_number, causing duplicates.
        return
      }

      Object.values(node).forEach((value) => visit(value))
    }

    visit(source)
    return found
  }

  const headers = [
    { label: 'Docket number', key: 'docket_number' },
    { label: 'Docket Date', key: 'docket_date' },
    { label: 'Order Number', key: 'order_number' },
    { label: 'Order Date', key: 'order_date' },
    { label: 'Customer Account Name', key: 'customer_account_name' },
    { label: 'Customer Term', key: 'customer_term' },
    { label: 'Order Type', key: 'order_type' },
    { label: 'Location', key: 'location' },
    { label: 'Weight', key: 'weight' },
    { label: 'Tons Ordered', key: 'tons_ordered' },
    { label: 'Tons Actual', key: 'tons_actual' },
    { label: 'File', key: 'file' },
  ]

  const getDataReport = (data) => {
    if (!data || !Array.isArray(data) || data.length !== 2) {
      setError('')
      setReportRows([])
      setDateRange(null)
      return
    }

    setDateRange(data)
    setLoadingDate(true)

    // The backend filters by order-creation date, not docket date, so a narrow
    // query would silently drop dockets whose parent order was created outside
    // the selected window. Always fetch the full dataset and filter client-side
    // by docket date so no dockets are left behind.
    const broadRange = [new Date(2000, 0, 1), new Date(2100, 11, 31)]

    axios
      .post('/api/get_custom_report', { data: broadRange })
      .then((res) => {
        const orders = [
          ...collectOrders(res.data.csv),
          ...collectOrders(res.data.orders),
          ...collectOrders(res.data.docketreport),
        ]

        const rows = buildReportRows(orders, data)

        if (rows.length === 0) {
          setError('No data found')
          setReportRows([])
        } else {
          setError('')
          setReportRows(rows)
        }
      })
      .catch(() => {
        setError('Error fetching report data')
        setReportRows([])
      })
      .finally(() => {
        setLoadingDate(false)
      })
  }

  return (
    <>
      <h1 className="mb-4">Docket Report</h1>
      <div className="d-flex align-items-end flex-wrap gap-3">
        <div className="d-flex flex-column">
          <DateRangePicker
            appearance="default"
            placeholder="Select Date Range"
            style={{ width: 260 }}
            onChange={(range) => getDataReport(range)}
            value={dateRange}
          />
        </div>
        <div>
          {loadingDate && <div className="btn btn-block btn-outline-info btn-sm">Loading....</div>}
          {!loadingDate && error && (
            <div className="btn btn-block btn-outline-info btn-sm">{error}</div>
          )}
          {!loadingDate && reportRows.length > 0 && dateRange && (
            <CSVLink
              className="btn btn-block btn-outline-info btn-sm"
              headers={headers}
              data={reportRows}
              filename={`docket-report-${formatDateRange(dateRange)}.csv`}
            >
              Download CSV
            </CSVLink>
          )}
        </div>
      </div>
    </>
  )
}

export default DocketReport
